import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import apollo from "@/helpers/apollo";
import { cloudinaryImageUrl } from "@/helpers/cloudinary-url";
import Location from "@/pages/location";
import { QUERY } from "@/pages/location/graphql";
import { getLocationMetaProps } from "@/pages/location/helpers/get-meta-props";
import type { DataType, VariablesType } from "@/pages/location/types";

const fetchLocation = createServerFn({ method: "GET" })
  .inputValidator((data: { company: string; location: string }) => data)
  .handler(async ({ data }) => {
    const client = apollo();
    const response = await client.query<DataType, VariablesType>({
      fetchPolicy: "no-cache",
      query: QUERY,
      variables: {
        companySlug: data.company,
        isUser: false,
        locationSlug: data.location,
      },
    });

    if (!response.data.company?.locations.length) {
      throw notFound();
    }

    return { data: response.data };
  });

export const Route = createFileRoute("/location/$company/$location")({
  component: LocationPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    const { company } = loaderData.data;
    const location = company.locations[0];
    const { description, title } = getLocationMetaProps(location);
    const url = `${process.env.APP_URL}/location/${company.slug}/${location.slug}`;
    const imageId = location.games
      .flatMap((g) => g.gameimageSet || [])
      .find((img) => img.imageId)?.imageId;

    return {
      links: [{ href: url, rel: "canonical" }],
      meta: [
        { title },
        { content: description, name: "description" },
        { content: description, property: "og:description" },
        { content: title, property: "og:title" },
        ...(imageId
          ? [{ content: cloudinaryImageUrl(imageId), property: "og:image" }]
          : []),
      ],
    };
  },
  loader: ({ params }) =>
    fetchLocation({
      data: { company: params.company, location: params.location },
    }),
});

function LocationPage() {
  const { data } = Route.useLoaderData();
  return <Location data={data} />;
}
